import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
  type OAuthApi,
} from '@backstage/core-plugin-api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FileChangePreview {
  filePath: string;
  checkId: string;
  checkName: string;
  diff: string;
  additions: number;
  deletions: number;
}

export interface AgentCheckPreview {
  checkId: string;
  checkName: string;
  description: string;
}

export interface PreviewResponse {
  files: FileChangePreview[];
  agentChecks: AgentCheckPreview[];
  suggestions: { annotations: Record<string, string> };
  parsedRepo: { kind: string; host?: string; owner?: string; repo?: string };
}

export interface FixResultItem {
  checkId: string;
  checkName: string;
  fixType: 'programmatic' | 'agent';
  prUrl?: string;
  workflowRunUrl?: string;
  error?: string;
}

export interface ApplyResponse {
  results: FixResultItem[];
}

export interface FactRecord {
  id: string;
  scope: string;
  status: string;
  evidence: string;
}

export interface CheckMeta {
  name: string;
  remediationAction?: string;
}

export interface PreviewInput {
  entityRef: string;
  checkIds: string[];
  checkMetas?: Record<string, CheckMeta>;
  annotationOverrides?: Record<string, string>;
}

export interface ApplyInput {
  entityRef: string;
  checkIds: string[];
  checkMetas?: Record<string, CheckMeta>;
  branchName?: string;
  annotationOverrides?: Record<string, string>;
}

export interface ScanStatus {
  lastDispatchedAt: string | null;
  lastReceivedAt: string | null;
  workflowRunUrl: string | null;
}

export interface DispatchResult {
  dispatched: boolean;
  workflowRunUrl: string | null;
}

export interface GlobalSettings {
  dispatchEnabled: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

// ---------------------------------------------------------------------------
// API ref + interface
// ---------------------------------------------------------------------------

export interface AiReadinessFixApi {
  preview(input: PreviewInput): Promise<PreviewResponse>;
  apply(input: ApplyInput): Promise<ApplyResponse>;
  fetchFacts(repoOwner: string, repoName: string): Promise<FactRecord[]>;
  getScanStatus(repoOwner: string, repoName: string): Promise<ScanStatus>;
  dispatchScan(repoOwner: string, repoName: string, repoUrl: string): Promise<DispatchResult>;
  /** Admin-only: read the instance-wide AI readiness settings (e.g. the event-driven dispatch toggle). */
  getGlobalSettings(): Promise<GlobalSettings>;
  /** Admin-only: toggle the instance-wide event-driven dispatch setting. */
  setDispatchEnabled(enabled: boolean): Promise<GlobalSettings>;
}

export const aiReadinessFixApiRef = createApiRef<AiReadinessFixApi>({
  id: 'plugin.rithum-ai-readiness-fix.service',
});

// ---------------------------------------------------------------------------
// Client implementation
// ---------------------------------------------------------------------------

export class AiReadinessFixClient implements AiReadinessFixApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly githubAuthApi?: OAuthApi,
  ) {}

  private async baseUrl(): Promise<string> {
    const raw = await this.discoveryApi.getBaseUrl('rithum-ai-readiness');
    return raw.replace(/\/$/, '');
  }

  private async request<T>(
    path: string,
    body: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const url = `${await this.baseUrl()}${path}`;
    const res = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...extraHeaders,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return (await res.json()) as T;
  }

  async fetchFacts(repoOwner: string, repoName: string): Promise<FactRecord[]> {
    const url = `${await this.baseUrl()}/report/facts?repoOwner=${encodeURIComponent(repoOwner)}&repoName=${encodeURIComponent(repoName)}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch facts: ${res.status}`);
    const data = (await res.json()) as { facts: FactRecord[] };
    return data.facts;
  }

  async preview(input: PreviewInput): Promise<PreviewResponse> {
    return this.request<PreviewResponse>('/fix/preview', input);
  }

  async apply(input: ApplyInput): Promise<ApplyResponse> {
    let extraHeaders: Record<string, string> = {};
    if (this.githubAuthApi) {
      try {
        const token = await this.githubAuthApi.getAccessToken(['repo']);
        if (token) {
          extraHeaders = { 'X-GitHub-Token': token };
        }
      } catch {
        // Fall back to App installation token — backend will use it automatically
      }
    }
    return this.request<ApplyResponse>('/fix/apply', input, extraHeaders);
  }

  async getScanStatus(repoOwner: string, repoName: string): Promise<ScanStatus> {
    const url = `${await this.baseUrl()}/scan/status?repoOwner=${encodeURIComponent(repoOwner)}&repoName=${encodeURIComponent(repoName)}`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch scan status: ${res.status}`);
    return (await res.json()) as ScanStatus;
  }

  async dispatchScan(repoOwner: string, repoName: string, repoUrl: string): Promise<DispatchResult> {
    return this.request<DispatchResult>('/scan/dispatch', { repoOwner, repoName, repoUrl });
  }

  async getGlobalSettings(): Promise<GlobalSettings> {
    const url = `${await this.baseUrl()}/admin/settings`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Failed to fetch global settings: ${res.status}`);
    }
    return (await res.json()) as GlobalSettings;
  }

  async setDispatchEnabled(enabled: boolean): Promise<GlobalSettings> {
    const url = `${await this.baseUrl()}/admin/settings`;
    const res = await this.fetchApi.fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dispatchEnabled: enabled }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Failed to update global settings: ${res.status}`);
    }
    return (await res.json()) as GlobalSettings;
  }
}
